import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { NATIONALITIES } from '../select-country/country.model';

interface NationalityOption {
  code: string;
  name: string;
}

@Component({
  selector: 'app-select-nationality',
  imports: [FormsModule, CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-nationality.html',
  styleUrl: './select-nationality.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectNationality),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectNationality),
      multi: true,
    },
  ],
})
export class SelectNationality implements ControlValueAccessor, Validator, OnInit, DoCheck {
  private injector = inject(Injector);

  @Input() label = 'Nationalite';
  @Input() value: string | undefined;
  @Input() required: boolean | string | null | undefined;
  @Input() showFlag = true;

  ngControl?: NgControl;
  disabled = false;
  cdr = inject(ChangeDetectorRef);

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect;

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const ctrl = this.control;
      return !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty));
    },
  };

  nationalityList: NationalityOption[] = [];

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de definir le valueAccessor. Assurez-vous que ce composant est utilise dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }

    this.nationalityList = Object.entries(NATIONALITIES)
      .map(([code, values]) => ({
        code,
        name: values.female,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  ngDoCheck(): void {
    this.matSelect?.updateErrorState();
  }

  onChange: (value: string | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: string | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.isRequired || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null && this.required !== '') {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }

    return this.control?.hasValidator(Validators.required) ?? false;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
  }
}
